import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ConsoleShell from "./ConsoleShell";
import LegacyPlatformRedirect from "./LegacyPlatformRedirect";
import WorkspaceDetailPage from "./WorkspaceDetailPage";
import PlatformWorkspaceCreatePage from "./PlatformWorkspaceCreatePage";
import PlatformPresencePage from "./PlatformPresencePage";
import PlatformDashboardPage from "./PlatformDashboardPage";
import PlatformConsoleSectionGate from "./PlatformConsoleSectionGate";
import { PLATFORM_CONSOLE_BASE, PLATFORM_CONSOLE_SEGMENT } from "./platformConsoleRoutes";
import {
  FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED,
  FEATURE_PLATFORM_INTERNAL_CHAT_ENABLED,
} from "../../../constants/featureFlags";
import {
  WorkspacesSectionPage,
  InsightsSectionPage,
  ProductUsageSectionPage,
  PassengerTicketSalesSectionPage,
  OpportunitiesSectionPage,
  SegmentsSectionPage,
  GovernanceSectionPage,
  CatalogSectionPage,
  ChatbotTemplatesSectionPage,
} from "./PlatformConsoleSectionPages";
import PendingActionsSectionPage from "./PendingActionsSectionPage";
import PlatformErrorsSectionPage from "./PlatformErrorsSectionPage";
import DbConnectionPressureSectionPage from "./DbConnectionPressureSectionPage";
import ApifacilInstancesSectionPage from "./ApifacilInstancesSectionPage";
import InternalAskSectionPage from "./InternalAskSectionPage";
import FinanceSectionPage from "./FinanceSectionPage";
import MyIncentivesPage from "./MyIncentivesPage";
import PortalTeamPage from "../../PortalTeam/PortalTeamPage";
import WhatsappOut2026SectionPage from "./WhatsappOut2026SectionPage";

/**
 * Console da plataforma (master xprobase). Rotas sob /platform/xprobase/*
 * Navegação por seções (drawer) + ?tab= legado redirecionado no index.
 *
 * Dashboard é import eager (não lazy): no 1º clique via SPA o chunk suspenso
 * podia montar sem outlet context e derrubar a árvore (tela branca até F5).
 */
const PlatformXprobaseConsole = () => {
  return (
    <Routes>
        <Route element={<ConsoleShell />}>
          <Route index element={<LegacyPlatformRedirect />} />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.workspaceCreate}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.workspaces} action="create">
                <PlatformWorkspaceCreatePage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={`${PLATFORM_CONSOLE_SEGMENT.workspaces}/:dominioId/:section?`}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.workspaces}>
                <WorkspaceDetailPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.workspaces}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.workspaces}>
                <WorkspacesSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.whatsappOut2026}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.workspaces}>
                <WhatsappOut2026SectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.opportunities}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.opportunities}>
                <OpportunitiesSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.insights}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.insights}>
                <InsightsSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={`${PLATFORM_CONSOLE_SEGMENT.dashboard}/:tab`}
            element={
              FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED ? (
                <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.dashboard}>
                  <PlatformDashboardPage />
                </PlatformConsoleSectionGate>
              ) : (
                <Navigate to={`${PLATFORM_CONSOLE_BASE}/workspaces`} replace />
              )
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.dashboard}
            element={
              <Navigate
                to={
                  FEATURE_PLATFORM_CONSOLIDATED_DASHBOARD_ENABLED
                    ? `${PLATFORM_CONSOLE_BASE}/dashboard/live`
                    : `${PLATFORM_CONSOLE_BASE}/workspaces`
                }
                replace
              />
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.productUsage}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.productUsage}>
                <ProductUsageSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.internalAsk}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.internalAsk}>
                <InternalAskSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.passengerTicketSales}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.passengerTicketSales}>
                <PassengerTicketSalesSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.presence}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.presence}>
                <PlatformPresencePage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.segments}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.segments}>
                <SegmentsSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.governance}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.governance}>
                <GovernanceSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.catalog}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.catalog}>
                <CatalogSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.chatbotTemplates}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.chatbotTemplates}>
                <ChatbotTemplatesSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.pendingActions}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.pendingActions}>
                <PendingActionsSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.platformErrors}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.platformErrors}>
                <PlatformErrorsSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.dbPressure}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.platformErrors}>
                <DbConnectionPressureSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.apifacilInstances}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.apifacilInstances}>
                <ApifacilInstancesSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route
            path={`${PLATFORM_CONSOLE_SEGMENT.finance}/:tab?`}
            element={
              <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.finance}>
                <FinanceSectionPage />
              </PlatformConsoleSectionGate>
            }
          />
          <Route path={PLATFORM_CONSOLE_SEGMENT.myIncentives} element={<MyIncentivesPage />} />
          <Route
            path={PLATFORM_CONSOLE_SEGMENT.team}
            element={
              FEATURE_PLATFORM_INTERNAL_CHAT_ENABLED ? (
                <PlatformConsoleSectionGate section={PLATFORM_CONSOLE_SEGMENT.presence}>
                  <PortalTeamPage mode="platform" />
                </PlatformConsoleSectionGate>
              ) : (
                <Navigate to={`${PLATFORM_CONSOLE_BASE}/workspaces`} replace />
              )
            }
          />
        </Route>
    </Routes>
  );
};

export default PlatformXprobaseConsole;
